// ─── GST BILL EXPORT (Excel + PDF) ────────────────────────────────────────────
// Shared by Reports → GST (every role) and Super Admin Tools. Both take the same
// normalized rows:
//   { invoiceNumber, date, guestName, company, companyGst, room,
//     taxable, cgst, sgst, totalGst, total }
// and hotel info { name, address, gstin, phone }.
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const blank = (v) => (v === null || v === undefined || v === '—' || v === 'N/A' ? '' : String(v));

// Dates arrive as 'YYYY-MM-DD' or 'YYYY-MM-DD HH:MM:SS' (MySQL dateStrings). Parse
// by hand — `new Date('2026-10-06 14:20:00')` is Invalid Date in Safari, and
// date-only strings shift a day in timezones behind UTC.
export function toDDMMYYYY(value) {
  const m = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : blank(value);
}

function sortedRows(rows) {
  return [...rows].sort((a, b) => String(a.date).localeCompare(String(b.date)) || String(a.invoiceNumber).localeCompare(String(b.invoiceNumber)));
}

export function gstTotals(rows) {
  return rows.reduce((t, r) => ({
    taxable: round2(t.taxable + Number(r.taxable || 0)),
    cgst: round2(t.cgst + Number(r.cgst || 0)),
    sgst: round2(t.sgst + Number(r.sgst || 0)),
    totalGst: round2(t.totalGst + Number(r.totalGst || 0)),
    total: round2(t.total + Number(r.total || 0)),
  }), { taxable: 0, cgst: 0, sgst: 0, totalGst: 0, total: 0 });
}

function saveBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

const safeFileName = (s) => String(s || 'GST_Report').replace(/[^\w.-]+/g, '_');

// Builds the .xlsx workbook. Exported separately so it can be tested without a browser.
export async function buildGstWorkbook({ rows, hotelInfo = {}, periodLabel = '' }) {
  const { default: ExcelJS } = await import('exceljs');
  const wb = new ExcelJS.Workbook();
  wb.created = new Date();
  const ws = wb.addWorksheet('GST Invoices', {
    views: [{ state: 'frozen', ySplit: 5 }],
    pageSetup: { orientation: 'landscape', paperSize: 9, fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
  });

  const columns = [
    { header: 'S.No', key: 'sno', width: 6 },
    { header: 'Invoice No', key: 'invoiceNumber', width: 22 },
    { header: 'Invoice Date', key: 'date', width: 13 },
    { header: 'Guest Name', key: 'guestName', width: 26 },
    { header: 'Company', key: 'company', width: 28 },
    { header: 'Customer GSTIN', key: 'companyGst', width: 19 },
    { header: 'Room', key: 'room', width: 10 },
    { header: 'Taxable Value', key: 'taxable', width: 15 },
    { header: 'CGST', key: 'cgst', width: 12 },
    { header: 'SGST', key: 'sgst', width: 12 },
    { header: 'Total GST', key: 'totalGst', width: 13 },
    { header: 'Invoice Total', key: 'total', width: 15 },
  ];
  ws.columns = columns.map(({ key, width }) => ({ key, width }));
  const lastCol = columns.length;

  ws.mergeCells(1, 1, 1, lastCol);
  ws.getCell(1, 1).value = `${hotelInfo.name || 'GST Report'} — GST Invoice Register`;
  ws.getCell(1, 1).font = { bold: true, size: 14 };
  ws.mergeCells(2, 1, 2, lastCol);
  ws.getCell(2, 1).value = [hotelInfo.address, hotelInfo.gstin ? `GSTIN: ${hotelInfo.gstin}` : '', hotelInfo.phone ? `Phone: ${hotelInfo.phone}` : ''].filter(Boolean).join('  |  ');
  ws.mergeCells(3, 1, 3, lastCol);
  ws.getCell(3, 1).value = `Period: ${periodLabel}    Generated: ${new Date().toLocaleString('en-IN')}`;
  ws.getCell(3, 1).font = { italic: true, color: { argb: 'FF555555' } };

  const header = ws.getRow(5);
  columns.forEach((c, i) => { header.getCell(i + 1).value = c.header; });
  header.font = { bold: true, color: { argb: 'FFFFFFFF' } };
  header.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
  header.height = 22;
  header.eachCell(cell => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF3C3C3C' } };
  });

  const money = '#,##0.00';
  sortedRows(rows).forEach((r, idx) => {
    const row = ws.addRow({
      sno: idx + 1,
      invoiceNumber: blank(r.invoiceNumber),
      date: toDDMMYYYY(r.date),
      guestName: blank(r.guestName),
      company: blank(r.company),
      companyGst: blank(r.companyGst),
      room: blank(r.room),
      taxable: round2(r.taxable),
      cgst: round2(r.cgst),
      sgst: round2(r.sgst),
      totalGst: round2(r.totalGst),
      total: round2(r.total),
    });
    ['taxable', 'cgst', 'sgst', 'totalGst', 'total'].forEach(k => { row.getCell(k).numFmt = money; });
    row.getCell('sno').alignment = { horizontal: 'center' };
  });

  const t = gstTotals(rows);
  const totalRow = ws.addRow({ guestName: `TOTAL (${rows.length} invoices)`, taxable: t.taxable, cgst: t.cgst, sgst: t.sgst, totalGst: t.totalGst, total: t.total });
  totalRow.font = { bold: true };
  totalRow.eachCell(cell => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEBEBEB' } };
  });
  ['taxable', 'cgst', 'sgst', 'totalGst', 'total'].forEach(k => { totalRow.getCell(k).numFmt = money; });

  for (let r = 5; r <= ws.rowCount; r++) {
    for (let c = 1; c <= lastCol; c++) {
      ws.getCell(r, c).border = {
        top: { style: 'thin', color: { argb: 'FFCCCCCC' } }, bottom: { style: 'thin', color: { argb: 'FFCCCCCC' } },
        left: { style: 'thin', color: { argb: 'FFCCCCCC' } }, right: { style: 'thin', color: { argb: 'FFCCCCCC' } },
      };
    }
  }
  return wb;
}

export async function downloadGstExcel({ rows, hotelInfo, periodLabel, fileName }) {
  const wb = await buildGstWorkbook({ rows, hotelInfo, periodLabel });
  const buffer = await wb.xlsx.writeBuffer();
  saveBlob(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `${safeFileName(fileName)}.xlsx`);
}

// Indian digit grouping without the ₹ glyph — jsPDF's built-in Helvetica can't
// render ₹ and prints garbage in its place.
const inr = (n) => round2(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Builds the PDF (A4 landscape). Exported separately so it can be tested without a browser.
export function buildGstPdf({ rows, hotelInfo = {}, periodLabel = '' }) {
  const doc = new jsPDF('l', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 10;
  const generated = new Date().toLocaleString('en-IN');

  const drawHeader = () => {
    doc.setFont('helvetica', 'bold').setFontSize(14).setTextColor(30);
    doc.text(String(hotelInfo.name || 'GST Report').toUpperCase(), marginX, 13);
    doc.setFont('helvetica', 'normal').setFontSize(8.5).setTextColor(90);
    const sub = [hotelInfo.address, hotelInfo.gstin ? `GSTIN: ${hotelInfo.gstin}` : '', hotelInfo.phone ? `Phone: ${hotelInfo.phone}` : ''].filter(Boolean).join('   |   ');
    doc.text(doc.splitTextToSize(sub, pageWidth - marginX * 2 - 70), marginX, 18.5);
    doc.setFont('helvetica', 'bold').setFontSize(10).setTextColor(30);
    doc.text('GST INVOICE REGISTER', pageWidth - marginX, 13, { align: 'right' });
    doc.setFont('helvetica', 'normal').setFontSize(8.5).setTextColor(90);
    doc.text(`Period: ${periodLabel}`, pageWidth - marginX, 18.5, { align: 'right' });
    doc.setDrawColor(190).setLineWidth(0.3).line(marginX, 25, pageWidth - marginX, 25);
  };

  const t = gstTotals(rows);
  const body = sortedRows(rows).map((r, i) => [
    i + 1, blank(r.invoiceNumber), toDDMMYYYY(r.date), blank(r.guestName), blank(r.company),
    blank(r.companyGst), inr(r.taxable), inr(r.cgst), inr(r.sgst), inr(r.totalGst), inr(r.total),
  ]);

  // Column widths add up to the printable width (297 - 2*10 = 277mm) exactly, so
  // nothing spills past the right margin.
  autoTable(doc, {
    startY: 29,
    margin: { top: 29, left: marginX, right: marginX, bottom: 16 },
    head: [['S.No', 'Invoice No', 'Date', 'Guest Name', 'Company', 'Customer GSTIN', 'Taxable (Rs.)', 'CGST (Rs.)', 'SGST (Rs.)', 'Total GST (Rs.)', 'Invoice Total (Rs.)']],
    body,
    foot: [[
      { content: `TOTAL  (${rows.length} invoices)`, colSpan: 6, styles: { halign: 'right' } },
      inr(t.taxable), inr(t.cgst), inr(t.sgst), inr(t.totalGst), inr(t.total),
    ]],
    showFoot: 'lastPage',
    theme: 'grid',
    styles: { font: 'helvetica', fontSize: 8.5, cellPadding: 1.8, valign: 'middle', lineColor: [205, 205, 205], textColor: [30, 30, 30] },
    headStyles: { fillColor: [60, 60, 60], textColor: 255, fontStyle: 'bold', halign: 'center', fontSize: 8.5 },
    footStyles: { fillColor: [235, 235, 235], textColor: [0, 0, 0], fontStyle: 'bold', halign: 'right' },
    columnStyles: {
      0: { cellWidth: 11, halign: 'center' },
      1: { cellWidth: 36, overflow: 'visible' },
      2: { cellWidth: 21, halign: 'center' },
      3: { cellWidth: 40 },
      4: { cellWidth: 42 },
      5: { cellWidth: 33 },
      6: { cellWidth: 21, halign: 'right' },
      7: { cellWidth: 17, halign: 'right' },
      8: { cellWidth: 17, halign: 'right' },
      9: { cellWidth: 17, halign: 'right' },
      10: { cellWidth: 22, halign: 'right' },
    },
    didDrawPage: drawHeader,
  });

  // Summary block under the table (new page if it doesn't fit).
  let y = doc.lastAutoTable.finalY + 10;
  if (y + 45 > pageHeight - 16) { doc.addPage(); drawHeader(); y = 34; }
  autoTable(doc, {
    startY: y,
    margin: { left: pageWidth - marginX - 110, right: marginX },
    body: [
      ['Total Invoices', String(rows.length)],
      ['Total Taxable Value', `Rs. ${inr(t.taxable)}`],
      ['CGST', `Rs. ${inr(t.cgst)}`],
      ['SGST', `Rs. ${inr(t.sgst)}`],
      ['Total GST', `Rs. ${inr(t.totalGst)}`],
      ['Gross Total (incl. GST)', `Rs. ${inr(t.total)}`],
    ],
    theme: 'grid',
    styles: { fontSize: 9.5, cellPadding: 2.2, textColor: [30, 30, 30] },
    columnStyles: { 0: { cellWidth: 60, fontStyle: 'bold', fillColor: [246, 246, 246] }, 1: { cellWidth: 50, halign: 'right', fontStyle: 'bold' } },
    didDrawPage: drawHeader,
  });

  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal').setFontSize(7.5).setTextColor(130);
    doc.text(`Generated ${generated}`, marginX, pageHeight - 7);
    doc.text(`Page ${i} of ${pages}`, pageWidth - marginX, pageHeight - 7, { align: 'right' });
  }
  return doc;
}

export function downloadGstPdf({ rows, hotelInfo, periodLabel, fileName }) {
  const doc = buildGstPdf({ rows, hotelInfo, periodLabel });
  doc.save(`${safeFileName(fileName)}.pdf`);
}

// Hotel objects come snake_case from the API, sometimes camelCased by callers.
export function hotelInfoFrom(h = {}) {
  return {
    name: h.gst_name || h.gstName || h.name || '',
    address: [h.address, h.city].filter(Boolean).join(', '),
    gstin: h.gst_number || h.gstNumber || '',
    phone: h.phone || '',
  };
}
